/**
 * Die acht Besitzkarten.
 *
 * Quelle: Besitzkarten der Referenzausgabe (siehe wiki/FuenfSterne/topics/spieldaten.md).
 * Diese Datei ist die einzige Wahrheit fuer alle Zahlen. Der Test
 * `test/spieldaten-wiki.test.ts` vergleicht sie mit der Wikiseite, damit
 * Wissensbasis und Code nicht auseinanderlaufen koennen.
 */

/** Eine Ausbaustufe: ein Gebaeude oder der Freizeitpark. */
export interface Stufe {
  /** Anzeigename der Stufe, wie er auf der Besitzkarte steht. */
  readonly bezeichnung: string
  /** Baukosten dieser Stufe an die Bank. */
  readonly kosten: number
  /** Sternebewertung des Hotels, sobald diese Stufe steht. Reine Anzeige. */
  readonly sterne: number
  /** Preis pro Uebernachtung, sobald diese Stufe steht. Die Rechengroesse. */
  readonly nachtsatz: number
}

export interface Hotel {
  readonly id: HotelId
  readonly name: string
  /** Name des Hotels in der Vorlage - nur zur Nachvollziehbarkeit dokumentiert. */
  readonly vorlage: string
  /** Grundfarbe des Gelaendes, auch im 3D-Rendering benutzt. */
  readonly farbe: string
  readonly grundstueck: number
  readonly zwangsverkauf: number
  readonly eingang: number
  /** Hauptgebaeude zuerst, dann die Nebengebaeude in Baureihenfolge. */
  readonly gebaeude: readonly Stufe[]
  readonly freizeitpark: Stufe
}

export type HotelId =
  | 'pagode'
  | 'koralle'
  | 'belvedere'
  | 'kapitol'
  | 'regent'
  | 'lagune'
  | 'maharani'
  | 'savanne'

const g = (bezeichnung: string, kosten: number, sterne: number, nachtsatz: number): Stufe => ({
  bezeichnung,
  kosten,
  sterne,
  nachtsatz,
})

export const HOTELS: Readonly<Record<HotelId, Hotel>> = {
  pagode: {
    id: 'pagode',
    name: 'Pagode',
    vorlage: 'Fujiyama',
    farbe: '#e8917c',
    grundstueck: 1000,
    zwangsverkauf: 500,
    eingang: 100,
    gebaeude: [
      g('Hauptgebaeude', 2200, 1, 100),
      g('Nebengebaeude 1', 1400, 1, 100),
      g('Nebengebaeude 2', 1400, 2, 200),
    ],
    freizeitpark: g('Freizeitpark', 500, 3, 400),
  },
  koralle: {
    id: 'koralle',
    name: 'Koralle',
    vorlage: 'Boomerang',
    farbe: '#f08b2e',
    grundstueck: 500,
    zwangsverkauf: 250,
    eingang: 100,
    gebaeude: [g('Hauptgebaeude', 1800, 1, 400)],
    freizeitpark: g('Freizeitpark', 250, 2, 600),
  },
  belvedere: {
    id: 'belvedere',
    name: 'Belvedere',
    vorlage: "L'Etoile",
    farbe: '#c6b892',
    grundstueck: 3000,
    zwangsverkauf: 1500,
    eingang: 250,
    gebaeude: [
      g('Hauptgebaeude', 3300, 1, 150),
      g('Nebengebaeude 1', 2200, 2, 300),
      g('Nebengebaeude 2', 1800, 2, 300),
      g('Nebengebaeude 3', 1800, 2, 300),
      g('Dependance', 1800, 3, 450),
    ],
    freizeitpark: g('Freizeitpark', 4000, 4, 750),
  },
  kapitol: {
    id: 'kapitol',
    name: 'Kapitol',
    vorlage: 'President',
    farbe: '#4f79c4',
    grundstueck: 3500,
    zwangsverkauf: 1750,
    eingang: 250,
    gebaeude: [
      g('Hauptgebaeude', 5000, 1, 200),
      g('Nebengebaeude 1', 3000, 2, 400),
      g('Nebengebaeude 2', 2250, 3, 600),
      g('Nebengebaeude 3', 1750, 4, 800),
    ],
    freizeitpark: g('Freizeitpark', 5000, 5, 1100),
  },
  regent: {
    id: 'regent',
    name: 'Regent',
    vorlage: 'Royal',
    farbe: '#37a05a',
    grundstueck: 2500,
    zwangsverkauf: 1250,
    eingang: 200,
    gebaeude: [
      g('Hauptgebaeude', 3600, 1, 150),
      g('Nebengebaeude 1', 2600, 2, 300),
      g('Nebengebaeude 2', 1800, 2, 300),
      g('Nebengebaeude 3', 1800, 3, 450),
    ],
    freizeitpark: g('Freizeitpark', 3000, 4, 600),
  },
  lagune: {
    id: 'lagune',
    name: 'Lagune',
    vorlage: 'Waikiki',
    farbe: '#f2c230',
    grundstueck: 2500,
    zwangsverkauf: 1250,
    eingang: 200,
    gebaeude: [
      g('Hauptgebaeude', 3500, 1, 200),
      g('Nebengebaeude 1', 2500, 2, 350),
      g('Nebengebaeude 2', 2500, 2, 500),
      g('Nebengebaeude 3', 1750, 3, 500),
      g('Dependance', 1750, 4, 650),
    ],
    freizeitpark: g('Freizeitpark', 2500, 5, 1000),
  },
  maharani: {
    id: 'maharani',
    name: 'Maharani',
    vorlage: 'Taj Mahal',
    farbe: '#b3592c',
    grundstueck: 1500,
    zwangsverkauf: 750,
    eingang: 100,
    gebaeude: [
      g('Hauptgebaeude', 2400, 1, 100),
      g('Nebengebaeude 1', 1000, 1, 100),
      g('Nebengebaeude 2', 500, 2, 200),
    ],
    freizeitpark: g('Freizeitpark', 1000, 3, 300),
  },
  savanne: {
    id: 'savanne',
    name: 'Savanne',
    vorlage: 'Safari',
    farbe: '#e0b06a',
    grundstueck: 2000,
    zwangsverkauf: 1000,
    eingang: 150,
    gebaeude: [
      g('Hauptgebaeude', 2600, 1, 100),
      g('Nebengebaeude 1', 1200, 1, 100),
      g('Nebengebaeude 2', 1200, 2, 250),
    ],
    freizeitpark: g('Freizeitpark', 2000, 3, 500),
  },
}

export const HOTEL_IDS = Object.keys(HOTELS) as readonly HotelId[]

/** Alle Stufen eines Hotels in Baureihenfolge, Freizeitpark zuletzt. */
export function stufen(hotel: Hotel): readonly Stufe[] {
  return [...hotel.gebaeude, hotel.freizeitpark]
}
